import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Loader,
  Notification,
  Message,
  ButtonOBSOLETED
} from '../../web.common';
import AriaModal from 'react-aria-modal';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import {
  DisplayDate,
  DisplayGuid
} from '../items';
import { DetailsRightBar, Action, Link, Divider } from './DetailsRightBar';

const configReader = require('../../config');

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to legacy path
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
}

class UserInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      userData: null,
      providerAdminId: null,
      agreementCount: null,
      signupCount: null,
      marketingConsent: null,
      emailDelivery: null,
      generatedPassword: null,
      passwordCopied: false,
      walletHistory: null
    };
  }

  async componentDidMount() {
    const { match: { params: { userId } } } = this.props;
    const { api: { users: { getUser }, providerAdmin: { getProviderAdminIdByUser }, automatedBookingAgreements: { getUserAgreementCount }, signups: { getUserSignupCount }, marketingConsent: { getMarketingConsent } } } = this.props;

    try {
      const userData = await getUser(userId);
      this.setState({ userData });
      const providerAdminData = await getProviderAdminIdByUser(userId);
      if (providerAdminData.success) {
        this.setState({ providerAdminId: providerAdminData.data });
      }
      const agreementCountData = await getUserAgreementCount(userId);
      if (agreementCountData) {
        this.setState({ agreementCount: agreementCountData });
      }
      const signupCountData = await getUserSignupCount(userId);
      if (signupCountData) {
        this.setState({ signupCount: signupCountData });
      }
      const marketingConsentData = await getMarketingConsent(userId);
      if (marketingConsentData.success) {
        this.setState({ marketingConsent: marketingConsentData.data });
      }
      await this.loadEmailDelivery();
    }
    catch (e) {
      this.setState({ errorMessage: new Message('error', 'Unable to get data') });
    }
    await this.loadWalletHistory();
  }

  async loadWalletHistory() {
    const { match: { params: { userId } } } = this.props;
    const { api: { users: { getWalletHistory } } } = this.props;
    try {
      const walletHistory = await getWalletHistory(userId, 1, 50);
      this.setState({ walletHistory });
    }
    catch (e) {
      // The rest of the page works without it.
      this.setState({ walletHistory: { error: `${e.message || e}` } });
    }
  }

  renderWalletHistory() {
    const { walletHistory } = this.state;
    const amount = value => (value == null ? '-' : Number(value).toFixed(2));

    let content = null;
    if (!walletHistory) {
      content = 'Loading…';
    }
    else if (walletHistory.error) {
      content = `Unable to load the wallet history (${walletHistory.error})`;
    }
    else if (walletHistory.length === 0) {
      content = 'No balance change recorded (the history starts on 27 Oct 2026).';
    }
    else {
      content = (
        <table className="data-lists component-data-table cell-content-align-top">
          <thead>
            <tr>
              <td>Date (CET)</td>
              <td>Kind</td>
              <td>Previous → New</td>
              <td>Delta</td>
              <td>Purchase / Transaction</td>
              <td>Admin</td>
              <td>Note</td>
            </tr>
          </thead>
          <tbody>
            {walletHistory.map(change => (
              <tr className="hover-listview" key={change.Id}>
                <td><DisplayDate breakTime>{change.CreatedAtCet}</DisplayDate></td>
                <td>{change.Kind}</td>
                <td>{`${amount(change.PreviousAmount)} → ${amount(change.NewAmount)}`}</td>
                <td style={{ color: change.Delta > 0 ? '#27ae60' : (change.Delta < 0 ? '#c0392b' : undefined) }}>
                  {`${change.Delta > 0 ? '+' : ''}${amount(change.Delta)}`}
                </td>
                <td>
                  {change.PurchaseId ? <DisplayGuid short baseAnchor="/purchase">{change.PurchaseId}</DisplayGuid> : '-'}
                  {change.TransactionId && (
                    <div><DisplayGuid short baseAnchor="/transaction">{change.TransactionId}</DisplayGuid></div>
                  )}
                </td>
                <td>{change.AdminName || '-'}</td>
                <td>{change.Note || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <article className="data-table-wrap">
        <h3 className="main-page-header main-page-header-with-subheader">
          Wallet history
        </h3>
        <br />
        {content}
      </article>
    );
  }

  async loadEmailDelivery() {
    const { match: { params: { userId } } } = this.props;
    const { api: { users: { getEmailDelivery } } } = this.props;
    try {
      const emailDelivery = await getEmailDelivery(userId);
      this.setState({ emailDelivery });
    }
    catch (e) {
      // Diagnostics only — the rest of the page works without them.
      this.setState({ emailDelivery: { error: `${e}` } });
    }
  }

  renderEmailDelivery() {
    const { emailDelivery } = this.state;
    if (!emailDelivery) {
      return 'Loading…';
    }
    if (emailDelivery.error) {
      return `Unavailable (${emailDelivery.error})`;
    }

    const suppressions = emailDelivery.suppressions || [];
    const lastEvent = (emailDelivery.recentEvents || [])[0];

    return (
      <span>
        {suppressions.length > 0
          ? (
            <span style={{ color: '#c0392b', fontWeight: 'bold' }}>
              {`SendGrid drops all mail to this address — on ${suppressions.map(s => `${s.list}${s.createdUtc ? ` since ${new Date(s.createdUtc).toLocaleDateString()}` : ''}`).join(', ')}`}
              {suppressions[0].reason ? ` (${suppressions[0].reason.slice(0, 160)})` : ''}
            </span>
          )
          : (emailDelivery.suppressionError ? `Suppression check failed: ${emailDelivery.suppressionError}` : 'Not suppressed')}
        <br />
        {lastEvent
          ? `Last code mail: ${lastEvent.event} · ${lastEvent.category} · ${new Date(lastEvent.timestampUtc).toLocaleString()}${lastEvent.reason ? ` · ${lastEvent.reason.slice(0, 160)}` : ''}`
          : 'No tracked code-mail events'}
      </span>
    );
  }

  render() {
    const { match: { params: { userId } } } = this.props;
    const { api: { users: { changeEmail, generatePassword, forgetUser, getUser, markEmailVerified, clearEmailSuppressions }, providerAdmin: { addUserToProviderAdmin, removeUserFromProviderAdmin }, marketingConsent: { resetMarketingConsent } } } = this.props;

    const { userData, errorMessage, providerAdminId, agreementCount, signupCount, marketingConsent, emailDelivery, generatedPassword, passwordCopied } = this.state;
    const { intercomAppId } = configReader.get();

    let agreementLinkText = 'Automated Booking Agreements';
    if (agreementCount) {
      const { active, total } = agreementCount;
      if (total === 0) {
        agreementLinkText = 'Automated Booking Agreements (none)';
      } else {
        agreementLinkText = `Automated Booking Agreements (${active}/${total} active)`;
      }
    }

    let signupLinkText = 'Signups';
    if (signupCount) {
      const { upcoming, total } = signupCount;
      if (total === 0) {
        signupLinkText = 'Signups (none)';
      } else {
        signupLinkText = `Signups (${upcoming}\u00A0upcoming\u00A0/\u00A0${total}\u00A0total)`;
        //signupLinkText = `Signups (${upcoming} upcoming / ${total} total)`;
      }
    }

    let mappedData = null;

    if (userData) {
      const {
        Id,
        Name,
        Email,
        PhoneNumber,
        CreatedAtCet,
        Wallet,
        HasPassword,
        EmailConfirmed,
        ConnectedProviders
      } = userData;

      const providersDisplay = ConnectedProviders && ConnectedProviders.length > 0
        ? ConnectedProviders.map(p => `${p.Provider} (${p.ProviderKey})`).join(', ')
        : 'None';

      mappedData = {
        Id: <DisplayGuid>{Id}</DisplayGuid>,
        Name,
        Email,
        'Email Verified': EmailConfirmed ? 'Yes' : <span style={{ color: '#e67e22', fontWeight: 'bold' }}>No</span>,
        'Email Delivery': this.renderEmailDelivery(),
        'Phone': PhoneNumber || '-',
        'Created At (CET)': <DisplayDate>{CreatedAtCet}</DisplayDate>,
        'Password': HasPassword ? 'Yes' : <span style={{ color: '#e67e22', fontWeight: 'bold' }}>No (social login only)</span>,
        'Connected Accounts': providersDisplay,
        'Marketing Consent': marketingConsent
          ? (
              marketingConsent.consented
                ? <span style={{ color: '#27ae60', fontWeight: 'bold' }}>
                    {`Yes — since ${marketingConsent.consentedAtUtc ? new Date(marketingConsent.consentedAtUtc).toLocaleDateString() : '?'} · source: ${marketingConsent.consentSource || '?'} · version: ${marketingConsent.consentVersion || '?'}`}
                  </span>
                : 'No'
            )
          : 'Loading…',
        Balance: `${(Wallet.amount ?? 0.0).toFixed(2)} DKK`,
        Intercom:
  <a
    target='_blank'
    rel='noopener noreferrer'
    href={`https://app.intercom.io/apps/${intercomAppId}/users/show?user_id=${Id}`}
  >
    Link
  </a>
      };
    }

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            {
              generatedPassword ? (
                <AriaModal
                  titleText='New password'
                  mounted
                  verticallyCenter
                  focusDialog
                  getApplicationNode={() => document.getElementById('app')}
                  dialogClass='dialog-size-normal no-min-height'
                  scrollDisabled
                >
                  <h4>The password has been saved. Copy it now: it is not shown again.</h4>
                  <div style={{ display: 'flex', gap: '8px', margin: '16px 0' }}>
                    <input
                      readOnly
                      value={generatedPassword}
                      onFocus={event => event.target.select()}
                      style={{ flex: 1, minWidth: 0, fontFamily: 'monospace' }}
                    />
                    <ButtonOBSOLETED
                      buttonColor='secondary'
                      size='small'
                      onClick={async () => {
                        // Copied on this button's own click, so the browser counts
                        // it as a user gesture and allows it.
                        const copied = await copyToClipboard(generatedPassword);
                        this.setState({ passwordCopied: copied });
                      }}
                      disabled={false}
                      buttonContent={passwordCopied ? 'Copied' : 'Copy'}
                      ariaLabel='copy password'
                    >
                      {passwordCopied ? 'Copied' : 'Copy'}
                    </ButtonOBSOLETED>
                  </div>
                  <ButtonOBSOLETED
                    buttonColor='gray'
                    size='small'
                    onClick={() => {
                      this.setState({ generatedPassword: null });
                    }}
                    disabled={false}
                    buttonContent='Close'
                    ariaLabel='close'
                  >
                    Close
                  </ButtonOBSOLETED>
                </AriaModal>
              ) : ''
            }
            <Notification newMessage={errorMessage} />

            <div className='content-with-right-pane-delimiter'>
              <PageTop
                documentTitle='User details'
                documentDescription='User details'
              />

              <h2 className='main-page-header main-page-header-with-subheader'>
                User
              </h2>
              <h5 className='main-page-subheader'>{userId}</h5>
              <br />

              {
                mappedData ? (
                  <React.Fragment>
                    <DataDefinitionList
                      listData={mappedData}
                      classNames={['detail-page-list']}
                    />
                    <br />
                    {this.renderWalletHistory()}
                  </React.Fragment>
                ) : <Loader hasLoaded={false}>Loading...</Loader>
              }
            </div>

            <DetailsRightBar>
              {
                mappedData ? (
                  <React.Fragment>
                    <Divider>Links</Divider>
                    <Link to={`/signups?user=${userData.Id}`}>{signupLinkText}</Link>
                    <Link to={`/purchases?userId=${userData.Id}`}>Purchases</Link>
                    <Link to={`/subscriptions?userId=${userData.Id}`}>Subscriptions</Link>
                    <Link to={`/participants?userId=${userData.Id}`}>Participants</Link>
                    <Link to={`/paymentmethods?userId=${userData.Id}`}>Payment Methods</Link>
                    {providerAdminId != null && (<Link to={`/provideradmin/${providerAdminId}`}>Provider Admin</Link>)}
                    <Link to={`/automated-booking-agreements?user=${userData.Id}`}>{agreementLinkText}</Link>
                    
                    <Divider>Actions</Divider>
                    <Action action={async () => {
                      try {
                        const email = prompt('Choose an email');

                        if (!email) {
                          return;
                        }

                        await changeEmail(email, userData.Id);

                        alert('Remember to cleanup cache from admin.wannasport.dk, maintenance section');

                        const userDataRefreshed = await getUser(userId);

                        this.setState({
                          userData: userDataRefreshed,
                          errorMessage: new Message('success', `email changed to ${email}`)
                        });
                      }
                      catch (e) {
                        this.setState({ errorMessage: new Message('error', `Unable to change email: ${e}`) });
                      }
                    }}
                    >
                      Change email
                    </Action>
                    <Action action={async () => {
                      try {
                        if (!window.confirm('Are you sure to generate a new password for this user?')) {
                          return;
                        }
                        const newPassword = await generatePassword(userData.Id);

                        this.setState({ generatedPassword: newPassword, passwordCopied: false });
                      }
                      catch (e) {
                        this.setState({ errorMessage: new Message('error', `Unable to change password: ${e}`) });
                      }
                    }
                    }
                    >
                      Generate Password
                    </Action>
                    <Action action={async () => {
                      try {
                        if (!window.confirm('Are you sure you want to remove all personal info about this user?')) {
                          return;
                        }
                        await forgetUser(userData.Id);

                        this.setState({
                          errorMessage: new Message('success', 'User personal info has been deleted')
                        });

                        const userDataUpdated = await getUser(userData.Id);
                        this.setState({ userData: userDataUpdated });
                      }
                      catch (e) {
                        this.setState({ errorMessage: new Message('error', `Unable to delete personal info: ${e}`) });
                      }
                    }
                    }
                    >
                      Forget User
                    </Action>
                    <Action action={async () => {
                      const amount = prompt('Set the amount');
                      const parsedAmount = parseFloat(amount);
                      if (isNaN(parsedAmount) || parsedAmount < 0.0) {
                        alert('Amont is not valid');
                        return;
                      }
                      // Optional; kept in the wallet history next to the admin's name.
                      const note = prompt('Reason / note (optional)');
                      if (note === null) {
                        return;
                      }
                      const { createWallet, setWallet } = this.props.api.seasonalcourts;
                      const { userData } = this.state;
                      const { Wallet } = userData;
                      const { Id } = userData;
                      try {
                        if (!Wallet) {
                          // `onlyPreview` is passed so withApi's appended language/timeZone land in place.
                          await createWallet(Id, false);
                        }
                        await setWallet(Id, parsedAmount, note.trim() || null);
                      }
                      catch (e) {
                        this.setState({ errorMessage: new Message('error', `Unable to set the balance: ${e.message || e}`) });
                        return;
                      }

                      const userDataUpdated = await getUser(userData.Id);
                      this.setState({ userData: userDataUpdated });
                      await this.loadWalletHistory();
                    }}>
                      Set Balance
                    </Action>
                    {!userData.EmailConfirmed && (
                      <Action action={async () => {
                        if (!window.confirm(`Mark ${userData.Email} as verified? Only do this when you are sure the user owns this address.`)) {
                          return;
                        }
                        try {
                          await markEmailVerified(userData.Id);
                          const userDataUpdated = await getUser(userData.Id);
                          this.setState({ userData: userDataUpdated, errorMessage: new Message('success', 'Email marked as verified') });
                          await this.loadEmailDelivery();
                        }
                        catch (e) {
                          this.setState({ errorMessage: new Message('error', `Unable to mark email as verified: ${e}`) });
                        }
                      }}>
                        Mark Email As Verified
                      </Action>
                    )}
                    {emailDelivery && emailDelivery.suppressions && emailDelivery.suppressions.length > 0 && (
                      <Action action={async () => {
                        if (!window.confirm('Remove this address from SendGrid\'s suppression lists so mail is sent again? If the mailbox really is dead it will bounce and be suppressed again.')) {
                          return;
                        }
                        try {
                          const response = await clearEmailSuppressions(userData.Id);
                          const cleared = (response && response.cleared) || [];
                          this.setState({ errorMessage: new Message('success', `Removed from: ${cleared.join(', ') || 'nothing'}`) });
                          await this.loadEmailDelivery();
                        }
                        catch (e) {
                          this.setState({ errorMessage: new Message('error', `Unable to clear suppressions: ${e}`) });
                        }
                      }}>
                        Clear Email Suppressions
                      </Action>
                    )}
                    {marketingConsent && marketingConsent.consented && (
                      <Action action={async () => {
                        if (!window.confirm('Reset this user\'s marketing-email consent to "not consented"? This does not re-enable it if they change their mind — only they can re-consent.')) {
                          return;
                        }
                        const response = await resetMarketingConsent(userData.Id);
                        if (!response.success) {
                          this.setState({ errorMessage: new Message('error', 'Unable to reset marketing consent') });
                          return;
                        }
                        this.setState({ marketingConsent: response.data, errorMessage: new Message('success', 'Marketing consent has been reset') });
                      }}>
                        Reset Marketing Consent
                      </Action>
                    )}
                    {providerAdminId == null && (
                      <Action action={async () => {
                        const response = await addUserToProviderAdmin(userData.Id);
                        if (!response.success) {
                          this.setState({ errorMessage: new Message('error', 'Unable to add user to provider admin') });
                          return;
                        }
                        this.setState({ providerAdminId: response.data, errorMessage: new Message('success', 'User has been added to provider admin') });
                        this.props.history.push(`/provideradmin/${response.data}`);
                      }}>
                        Add User To Provider Admin
                      </Action>
                    )}
                    {providerAdminId != null && (
                      <Action action={async () => {
                        const result = confirm(
                          `Are you sure to remove ${userData.Name} as admin?`
                        );
                        if (!result) {
                          return;
                        }

                        const response = await removeUserFromProviderAdmin(providerAdminId);
                        if (!response.success) {
                          this.setState({ errorMessage: new Message('error', 'Unable to remove user as admin') });
                          return;
                        }
                        this.setState({ providerAdminId: null, errorMessage: new Message('success', 'User has been removed as admin') });
                      }}>
                        Remove as Admin
                      </Action>
                    )}
                  </React.Fragment>
                ) : ''
              }
            </DetailsRightBar>
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const User = withRouter(withApi(connect(({ user }) => ({ isLoggedIn: !!user }), {})(UserInternal)));

User.requiresLogin = true;
User.propTypes = { isLoggedIn: PropTypes.bool };

export default User;
